'use client';

import { useEffect, useMemo, useState } from 'react';

const ZERO_COUNTDOWN = {
  totalMs: 0,
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00',
  isExpired: true,
};

function formatUnit(value) {
  return String(Math.max(0, value)).padStart(2, '0');
}

function buildCountdownState(targetMs) {
  if (!targetMs || Number.isNaN(targetMs)) {
    return ZERO_COUNTDOWN;
  }

  const totalMs = Math.max(targetMs - Date.now(), 0);
  const totalSeconds = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    totalMs,
    days: formatUnit(days),
    hours: formatUnit(hours),
    minutes: formatUnit(minutes),
    seconds: formatUnit(seconds),
    isExpired: totalMs <= 0,
  };
}

export function useCountdown(targetDate, { enabled = true } = {}) {
  const targetMs = useMemo(() => {
    if (!targetDate) return null;
    return typeof targetDate === 'number' ? targetDate : Date.parse(targetDate);
  }, [targetDate]);

  const [countdown, setCountdown] = useState(() => (
    enabled ? buildCountdownState(targetMs) : ZERO_COUNTDOWN
  ));

  useEffect(() => {
    if (!enabled || !targetMs) {
      setCountdown(ZERO_COUNTDOWN);
      return undefined;
    }

    setCountdown(buildCountdownState(targetMs));

    const timer = window.setInterval(() => {
      setCountdown(buildCountdownState(targetMs));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [enabled, targetMs]);

  return countdown;
}
